import { useContext, useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  AddHomeWork as AddHomeWorkIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import logo from "../assets/logo1.png";
import { useGetMyProfile } from "../hooks/query/useUser";

function ResponsiveDrawer(props: any) {
  const theme = useTheme();
  const { currentUser, logout } = useContext(AuthContext);
  const { data: profile } = useGetMyProfile();
  const navigate = useNavigate();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const drawerWidth = minimized ? 90 : 250;

  // Primary color
  const PRIMARY_COLOR = "#bb0000";
  const LIGHT_PRIMARY_COLOR = "rgba(187,0,0,0.1)";
  const BACKGROUND_COLOR = "#f8f8f8";

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const toggleMinimized = () => {
    setMinimized(!minimized);
  };

  const mainItems =
    currentUser.role.displayName === "Super Admin"
      ? [
          {
            text: "Dashboard",
            path: "/dashboard",
            icon: <DashboardIcon />,
          },
        ]
      : [
          {
            text: "Properties",
            path: "/properties",
            icon: <AddHomeWorkIcon />,
          },
        ];

  const settingsItem = {
    text: "Settings",
    path: "/settings",
    icon: <SettingsIcon />,
  };

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: `linear-gradient(135deg, ${BACKGROUND_COLOR} 0%, ${PRIMARY_COLOR}10 100%)`,
        boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
      }}
    >
      <Toolbar
        sx={{
          height: 128,
          display: "flex",
          justifyContent: minimized ? "center" : "space-between",
          alignItems: "center",
          px: 2,
        }}
      >
        {!minimized && (
          <img
            src={logo || "default-logo.png"}
            alt="Logo"
            onClick={() => navigate("/")}
            style={{
              height: "150px",
              maxWidth: "100%",
              width: "auto",
              transition: "all 0.3s ease",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
            }}
          />
        )}
        <IconButton
          onClick={toggleMinimized}
          sx={{
            color: PRIMARY_COLOR,
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: LIGHT_PRIMARY_COLOR,
              transform: "rotate(180deg)",
            },
          }}
        >
          {minimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <Divider sx={{ borderColor: `${PRIMARY_COLOR}30` }} />

      <List sx={{ flexGrow: 1, marginTop: "26px", padding: "0 16px" }}>
        {mainItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ marginBottom: 2 }}>
            <ListItemButton
              component={NavLink}
              to={item.path}
              sx={{
                borderRadius: "12px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: LIGHT_PRIMARY_COLOR,
                  transform: "translateX(5px)",
                },
                "&.active": {
                  backgroundColor: LIGHT_PRIMARY_COLOR,
                  color: PRIMARY_COLOR,
                  "& .MuiListItemIcon-root": {
                    color: PRIMARY_COLOR,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 48,
                  justifyContent: "center",
                  color: "rgba(0,0,0,0.6)",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!minimized && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: 600 },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: `${PRIMARY_COLOR}30` }} />

      <List sx={{ padding: "0 16px", marginBottom: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to={settingsItem.path}
            sx={{
              borderRadius: "12px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: LIGHT_PRIMARY_COLOR,
                transform: "translateX(5px)",
              },
              "&.active": {
                backgroundColor: LIGHT_PRIMARY_COLOR,
                color: PRIMARY_COLOR,
                "& .MuiListItemIcon-root": {
                  color: PRIMARY_COLOR,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 48,
                justifyContent: "center",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              {settingsItem.icon}
            </ListItemIcon>
            {!minimized && (
              <ListItemText
                primary={settingsItem.text}
                primaryTypographyProps={{
                  variant: "body2",
                  sx: { fontWeight: 600 },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex", backgroundColor: BACKGROUND_COLOR }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                display: { xs: "block", sm: "block", md: "none" },
                color: PRIMARY_COLOR,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  backgroundColor: LIGHT_PRIMARY_COLOR,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: 1,
            }}
          >
            <Avatar
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                backgroundColor: PRIMARY_COLOR,
                color: "#fff",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  boxShadow: `0 0 0 4px ${LIGHT_PRIMARY_COLOR}`,
                },
              }}
              aria-label="user-avatar"
              alt={profile?.name}
              src={profile?.profileImageUrl || undefined}
            >
              {!profile?.profileImageUrl && profile?.name?.[0]}
            </Avatar>
            <Typography
              variant="subtitle1"
              noWrap
              component="div"
              sx={{
                color: "rgba(0, 0, 0, 0.87)",
                fontWeight: 600,
                maxWidth: { xs: 100, sm: 150 },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentUser?.name}
            </Typography>
            <IconButton
              sx={{
                padding: "8px",
                color: PRIMARY_COLOR,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: LIGHT_PRIMARY_COLOR,
                  transform: "scale(1.1)",
                },
              }}
              onClick={handleMenuOpen}
            >
              <KeyboardArrowDownIcon fontSize="medium" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                "& .MuiPaper-root": {
                  borderRadius: 2,
                  marginTop: 1,
                  minWidth: 180,
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "& .MuiMenuItem-root": {
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: LIGHT_PRIMARY_COLOR,
                      color: PRIMARY_COLOR,
                      transform: "translateX(5px)",
                    },
                  },
                },
              }}
            >
              <MenuItem component={Link} to="/profile">
                <Typography variant="body2">Profile</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Typography variant="body2">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation drawer"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: "#f4f6f9",
        }}
      >
        <Toolbar />
        <Box
          sx={{
            p: 3,
            backgroundColor: "white",
            minHeight: "calc(100vh - 64px)",
            borderTopLeftRadius: 16,
            boxShadow: "0 -4px 6px rgba(0,0,0,0.05)",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Rest of the code remains the same as previous version */}
      {/* Drawer and Main content section ... */}
    </Box>
  );
}

export default ResponsiveDrawer;
